import { useChatEntityFeatureFlags } from "@/components/shared/chat-feature-flags-context";
import {
  isInteractionCustomType,
  resolveChatMessageContent,
} from "@/components/shared/chat-message-content-renderer";
import type {
  ChatMessage,
  ChatRenderContext,
} from "@/components/shared/chat-message-types";
import { GifMessageRenderer } from "@/components/conversations/gif-message-renderer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { HeicImage } from "@/components/ui/heic-image";
import { getDisplayText, MENTOR_LANGUAGE } from "@/lib/sendbird/translation";
import { cn, getInitials } from "@/lib/utils";
import { isMessageHidden } from "@/lib/utils/parse-message-data";
import {
  FileImageIcon,
  ImageIcon,
  Loader2,
  MicIcon,
  VideoIcon,
} from "lucide-react";
import { DateTime } from "luxon";
import { ReactNode, useState } from "react";

interface ChatMessageItemProps {
  message: ChatMessage;
  layout: {
    alignment: "left" | "right";
    messageStyle: "incoming" | "outgoing";
    isHighlighted?: boolean;
    dimWhenHidden?: boolean;
  };
  sender: {
    label: string;
    profileUrl?: string | null;
  };
  slots?: {
    bubbleAccessories?: ReactNode;
    reactions?: ReactNode;
    metaExtra?: ReactNode;
  };
  context?: ChatRenderContext;
  behavior?: {
    onParentMessageClick?: (message: ChatMessage) => void;
  };
}

export function ChatMessageItem({
  message,
  layout,
  sender,
  slots,
  context,
  behavior,
}: ChatMessageItemProps) {
  const {
    alignment,
    messageStyle,
    isHighlighted = false,
    dimWhenHidden = false,
  } = layout;
  const isHidden = isMessageHidden(message.data);
  const entityFlags = useChatEntityFeatureFlags();

  const specialContent = resolveChatMessageContent({
    message,
    context,
    entityFlags,
  });

  const shouldRenderMessageBody =
    !isHidden &&
    (!isInteractionCustomType(message.customType) ||
      message.text.trim().length > 0);

  if (!isHidden && specialContent?.kind === "hidden") {
    return null;
  }

  if (!isHidden && specialContent?.kind === "centered") {
    return (
      <div className="mb-4" data-sb-message-id={message.id}>
        {specialContent.content}
      </div>
    );
  }

  const formattedTime = DateTime.fromMillis(message.createdAt).toLocaleString(
    DateTime.TIME_SIMPLE,
  );

  const preBubble =
    !isHidden && specialContent?.kind === "preBubble"
      ? specialContent.content
      : null;

  return (
    <div
      className={cn(
        "mb-4 flex items-start",
        alignment === "right" ? "justify-end" : "justify-start",
        dimWhenHidden && isHidden && "opacity-60",
      )}
      data-sb-message-id={message.id}
    >
      <div
        className={cn(
          "order-2 mx-2 flex max-w-md flex-col items-start space-y-2 text-sm",
          alignment === "right" ? "items-end" : "items-start",
        )}
      >
        {message.parentMessage && (
          <QuotedMessageCard
            message={message.parentMessage}
            onClick={
              behavior?.onParentMessageClick
                ? () => behavior.onParentMessageClick?.(message.parentMessage!)
                : undefined
            }
          />
        )}
        {preBubble}
        <div
          className={cn("flex items-center gap-2", {
            "flex-row-reverse": alignment === "right",
          })}
        >
          {isHidden ? (
            <RedactedMessage />
          ) : shouldRenderMessageBody ? (
            <MessageBody
              message={message}
              messageStyle={messageStyle}
              isHighlighted={isHighlighted}
            />
          ) : null}
          {slots?.bubbleAccessories}
        </div>
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-row items-center">
            <Avatar className="h-5 w-5">
              <AvatarImage src={sender.profileUrl ?? undefined} />
              <AvatarFallback className="text-[8px]">
                {getInitials(sender.label)}
              </AvatarFallback>
            </Avatar>
            <span className="ml-1 text-xs font-medium">{sender.label}</span>
          </div>
          <div className="h-1 w-1 rounded-full bg-gray-600" />
          <span className="text-xs uppercase text-gray-500">
            {formattedTime}
          </span>
          {slots?.metaExtra}
        </div>
        {slots?.reactions}
      </div>
    </div>
  );
}

function MessageBody({
  message,
  messageStyle,
  isHighlighted,
}: {
  message: ChatMessage;
  messageStyle: "incoming" | "outgoing";
  isHighlighted: boolean;
}) {
  const isGifMessage = message.customType === "GIF_MESSAGE";
  const isFileMessage = message.kind === "file";

  if (isGifMessage) {
    return (
      <GifMessageRenderer
        gifUrl={message.text}
        isCurrentUser={messageStyle === "outgoing"}
      />
    );
  }

  if (isFileMessage) {
    return <AttachmentMessage message={message} messageStyle={messageStyle} />;
  }

  return (
    <TextMessage
      message={message}
      messageStyle={messageStyle}
      isHighlighted={isHighlighted}
    />
  );
}

function TextMessage({
  message,
  messageStyle,
  isHighlighted,
}: {
  message: ChatMessage;
  messageStyle: "incoming" | "outgoing";
  isHighlighted: boolean;
}) {
  const [showOriginal, setShowOriginal] = useState(false);
  const { text: translatedText, isTranslated } = getDisplayText(
    { message: message.text, translations: message.translations ?? {} },
    MENTOR_LANGUAGE,
  );
  const renderedText =
    isTranslated && showOriginal ? message.text : translatedText;

  const links: string[] = renderedText.match(/(https?:\/\/\S+)/g) || [];
  const textParts: string[] = renderedText.split(/(https?:\/\/\S+)/g);

  return (
    <div
      className={cn(
        "flex flex-1 flex-col",
        messageStyle === "outgoing" ? "items-end" : "items-start",
      )}
    >
      <div
        className={cn(
          "inline-block whitespace-pre-line break-words rounded-3xl px-4 py-2",
          messageStyle === "outgoing"
            ? "bg-blue-600 text-white"
            : "bg-zinc-100 text-black",
          message.customType === "SUPERVISION" && "bg-red-400 text-white",
          isHighlighted && "!bg-[#FFF5F5] !text-black ring-2 ring-[#FF6B6B]",
        )}
      >
        {textParts.map((part, index) => {
          if (links.includes(part)) {
            return (
              <a
                key={index}
                href={part}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  "mx-1 underline",
                  messageStyle === "outgoing" ? "text-white" : "text-blue-600",
                )}
              >
                {part}
              </a>
            );
          }

          return (
            <span key={index} className="inline-block">
              {part}
            </span>
          );
        })}
      </div>
      {isTranslated && (
        <button
          type="button"
          onClick={() => setShowOriginal((prev) => !prev)}
          className="mt-1 text-xs text-gray-500 hover:underline"
        >
          {showOriginal ? "Show translation" : "Translated · Show original"}
        </button>
      )}
    </div>
  );
}

function AttachmentMessage({
  message,
  messageStyle,
}: {
  message: ChatMessage;
  messageStyle: "incoming" | "outgoing";
}) {
  const mimeType = message.attachment?.mimeType ?? "";
  const url = message.attachment?.url;

  if (!url) {
    if (message.attachmentPending) {
      return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
    }
    return (
      <div
        className={cn(
          "rounded-3xl px-4 py-2 text-sm text-zinc-500",
          messageStyle === "outgoing" ? "bg-blue-600/20" : "bg-zinc-100",
        )}
      >
        Media unavailable
      </div>
    );
  }

  if (mimeType.includes("image")) {
    return (
      <HeicImage
        src={url}
        mimeType={mimeType}
        alt="message"
        className={cn(
          "rounded-lg",
          messageStyle === "outgoing"
            ? "bg-blue-600 text-white"
            : "bg-gray-300 text-gray-600",
        )}
      />
    );
  }

  if (mimeType.includes("audio")) {
    return <audio src={url} controls className="rounded-lg" />;
  }

  if (mimeType.includes("video") || mimeType.includes("mov")) {
    return (
      <video
        src={url}
        controls
        className={cn(
          "rounded-lg",
          messageStyle === "outgoing"
            ? "bg-blue-600 text-white"
            : "bg-gray-300 text-gray-600",
        )}
      />
    );
  }

  return null;
}

function RedactedMessage() {
  return (
    <div className="inline-block whitespace-pre-line break-words rounded-3xl bg-zinc-200 px-4 py-2 text-sm italic text-zinc-400">
      This message has been redacted
    </div>
  );
}

function QuotedMessageCard({
  message,
  onClick,
}: {
  message: ChatMessage;
  onClick?: () => void;
}) {
  return (
    <div
      className="-mb-4 flex cursor-pointer flex-row items-center justify-between gap-2 rounded-lg bg-gray-100 p-2 pb-4 active:bg-gray-200"
      onClick={onClick}
    >
      <div className="flex flex-grow flex-row items-center">
        <QuotedMessageContent message={message} />
      </div>
      <div className="flex items-center gap-2">
        <QuotedMessagePreview message={message} />
      </div>
    </div>
  );
}

function QuotedMessageContent({ message }: { message: ChatMessage }) {
  const mimeType = message.attachment?.mimeType ?? "";
  const isImage = mimeType.includes("image");
  const isAudio = mimeType.includes("audio");
  const isVideo = mimeType.includes("video");
  const isGif = message.customType === "GIF_MESSAGE";
  const { text: textContent } = getDisplayText(
    { message: message.text, translations: message.translations ?? {} },
    MENTOR_LANGUAGE,
  );

  return (
    <div className="flex max-w-md flex-col">
      <span className="text-sm font-semibold text-blue-500">
        {message.sender?.displayName ?? message.sender?.nickname ?? "Unknown"}
      </span>
      <div className="flex items-center gap-2">
        {isImage && <ImageIcon className="h-4 w-4 text-gray-500" />}
        {isAudio && <MicIcon className="h-4 w-4 text-gray-500" />}
        {isVideo && <VideoIcon className="h-4 w-4 text-gray-500" />}
        {isGif && <FileImageIcon className="h-4 w-4 text-gray-500" />}
        <span className="max-w-2xl truncate text-sm text-gray-700">
          {isImage && "Photo"}
          {isAudio && "Audio"}
          {isVideo && "Video"}
          {isGif && "GIF"}
          {!isImage && !isAudio && !isVideo && !isGif && textContent}
        </span>
      </div>
    </div>
  );
}

function QuotedMessagePreview({ message }: { message: ChatMessage }) {
  const mimeType = message.attachment?.mimeType ?? "";
  const isImage = mimeType.includes("image");
  const isAudio = mimeType.includes("audio");
  const isVideo = mimeType.includes("video");
  const isGif = message.customType === "GIF_MESSAGE";

  if (isGif) {
    return (
      <div className="h-10 w-10 overflow-hidden rounded">
        <img
          src={message.text}
          alt="GIF Preview"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  if (isImage && message.attachment?.url) {
    return (
      <div className="h-10 w-10 overflow-hidden rounded">
        <HeicImage
          src={message.attachment.url}
          mimeType={message.attachment.mimeType ?? undefined}
          alt="Preview"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <ImageIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <MicIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200">
        <VideoIcon className="h-6 w-6 text-gray-500" />
      </div>
    );
  }

  return null;
}
